<template>
  <article class="post-article">
    <header class="post-article-header">
      <component :is="heading">{{ post.title }}</component>
      <p v-if="formatDate(post.created_at)" class="post-date">
        {{ formatDate(post.created_at) }}
      </p>
    </header>

    <div class="post-body">
      <template v-for="(part, index) in parts" :key="index">
        <img
          v-if="part.type === 'image'"
          class="post-image"
          :src="part.url"
          :alt="part.alt"
        />
        <a
          v-else-if="part.type === 'link'"
          class="post-link"
          :href="part.url"
          target="_blank"
          rel="noopener noreferrer"
        >{{ part.text }}</a>
        <template v-else>{{ part.text }}</template>
      </template>
    </div>

    <div class="reaction-row">
      <button class="react-btn" type="button" @click="reactPost('like')">
        👍 {{ post.likes_count }}
      </button>
      <button class="react-btn" type="button" @click="reactPost('dislike')">
        👎 {{ post.dislikes_count }}
      </button>
    </div>

    <section class="comments">
      <h3>Comments</h3>
      <form class="comment-form" @submit.prevent="submitComment">
        <div class="comment-hp" aria-hidden="true">
          <label for="ns-leave-blank">Leave this blank</label>
          <input
            id="ns-leave-blank"
            v-model="form.ns_leave_blank"
            name="ns_leave_blank"
            tabindex="-1"
            autocomplete="off"
          />
        </div>
        <input v-model="form.author_name" placeholder="Name" required />
        <textarea v-model="form.body" placeholder="Comment" required rows="3" />
        <p v-if="formError" class="form-error">{{ formError }}</p>
        <button type="submit" class="btn">Post comment</button>
      </form>
      <ul class="comment-list">
        <li v-for="c in post.comments" :key="c.id" class="comment-item">
          <strong>{{ c.author_name }}</strong>
          <p>{{ c.body }}</p>
          <div class="reaction-row small">
            <button type="button" class="react-btn" @click="reactComment(c, 'like')">
              👍 {{ c.likes_count }}
            </button>
            <button type="button" class="react-btn" @click="reactComment(c, 'dislike')">
              👎 {{ c.dislikes_count }}
            </button>
          </div>
        </li>
        <li v-if="!post.comments?.length" class="empty">No comments yet.</li>
      </ul>
    </section>
  </article>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { BlogService } from "@/services/blog_service";
import { parsePostBody } from "@/lib/post_body";

const props = defineProps({
  post: { type: Object, required: true },
  heading: { type: String, default: "h2" }
});

const emit = defineEmits(["updated"]);

const blog = new BlogService();
const form = reactive({ author_name: "", body: "", ns_leave_blank: "" });
const formError = ref("");
const commentToken = ref("");
const parts = computed(() => parsePostBody(props.post?.body));

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

function touch() {
  emit("updated");
}

async function reactPost(reaction_type) {
  const pkg = await blog.reactToPost(props.post.id, reaction_type);
  if (pkg.post) {
    props.post.likes_count = pkg.post.likes_count;
    props.post.dislikes_count = pkg.post.dislikes_count;
    touch();
  }
}

async function reactComment(comment, reaction_type) {
  const pkg = await blog.reactToComment(comment.id, reaction_type);
  if (pkg.comment) {
    comment.likes_count = pkg.comment.likes_count;
    comment.dislikes_count = pkg.comment.dislikes_count;
  }
}

async function loadCommentToken() {
  commentToken.value = "";
  if (!props.post?.id) return;
  try {
    const pkg = await blog.getCommentToken(props.post.id);
    commentToken.value = pkg.token || "";
  } catch (e) {
    console.error(e);
  }
}

watch(() => props.post?.id, loadCommentToken, { immediate: true });

async function submitComment() {
  formError.value = "";
  const pkg = await blog.addComment(
    props.post.id,
    form.author_name,
    form.body,
    commentToken.value,
    form.ns_leave_blank
  );
  if (pkg.comment) {
    props.post.comments = [...(props.post.comments || []), pkg.comment];
    form.body = "";
    form.ns_leave_blank = "";
    touch();
  } else if (pkg.errors) {
    formError.value = Array.isArray(pkg.errors) ? pkg.errors.join(", ") : String(pkg.errors);
  } else {
    formError.value = "Comment failed.";
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/blog";
</style>
